/**
 * Utility functions for copying text to clipboard
 *
 * @format
 */

/**
 * Copy text to clipboard with fallback for older browsers
 * @param {string} text - Text to copy
 * @param {string} fieldName - Name of the field being copied (for user feedback)
 * @returns {Promise<boolean>} - Success status
 */
export const copyToClipboard = async (text, fieldName = "Text") => {
  try {
    // Try modern clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback for older browsers or non-secure contexts
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const result = document.execCommand("copy");
    document.body.removeChild(textArea);

    return result;
  } catch (err) {
    console.error("Failed to copy text: ", err);
    return false;
  }
};

/**
 * Get copy button styles based on copied state
 * @param {string} fieldName - Name of the field
 * @param {string} copiedField - Currently copied field
 * @returns {string} - CSS classes for the button
 */
export const getCopyButtonStyles = (fieldName, copiedField) => {
  const baseClasses = "ml-2 p-1.5 rounded-md transition-colors";

  if (copiedField === fieldName) {
    return `${baseClasses} bg-green-100 text-green-600`;
  }

  return `${baseClasses} bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800`;
};
